/**
 * Promo banner carousel for the customer portal.
 *
 * Two jobs, deliberately kept apart:
 *
 *  1. Visibility -- `PromoBanners.isEligible` decides whether a single promo
 *     block should be shown to the customer in front of us. It is a pure
 *     function of the block's config and the customer context, so it needs
 *     no DOM.
 *
 *  2. Presentation -- `<promo-carousel>` lays the eligible slides out as a
 *     scroll-snapped strip. Swipe comes free from native scrolling; the dots,
 *     arrows and autoplay drive the same `scrollTo`, so there is one source of
 *     truth for the current slide (the scroll position) and nothing to keep in
 *     sync when the customer flicks between them.
 *
 * The element renders nothing until `update()` has been called with the
 * customer context, because subscription state only resolves once the Recharge
 * store has loaded -- see updateBanners() in shared-account-scripts.liquid.
 */
(function () {
  'use strict';

  const TIER_TAGS = ['Tier A', 'Tier B', 'Tier C', 'Tier D'];

  /** "Tier A, Tier C" -> ["Tier A", "Tier C"]; blank -> []. */
  const parseList = (value) => String(value ?? '')
    .split(',')
    .map(part => part.trim())
    .filter(Boolean);

  /**
   * @param {{ audience?: string, tiers?: string[] }} rule    block config
   * @param {{ isActive?: boolean, tags?: string[] }} context customer context
   */
  const isEligible = (rule = {}, context = {}) => {
    rule = rule || {};
    context = context || {};

    const isActive = context.isActive === true;
    const audience = rule.audience || 'subscribers';

    if (audience === 'subscribers' && !isActive) return false;
    if (audience === 'non_subscribers' && isActive) return false;

    // No tier ticked means "every tier", so an untagged customer still sees it.
    const tiers = rule.tiers || [];
    if (tiers.length === 0) return true;

    const tags = context.tags || [];
    return tiers.some(tier => tags.includes(tier));
  };

  /** Reads the rule a slide carries as data attributes. */
  const ruleFromElement = (el) => ({
    audience: el.dataset.audience || 'subscribers',
    tiers: parseList(el.dataset.tiers)
  });

  /**
   * In the theme editor every block must stay visible, otherwise a merchant
   * adding a banner for a tier they are not in sees nothing happen.
   */
  const isDesignMode = () => typeof Shopify !== 'undefined' && !!Shopify.designMode;

  /** The slides to show, in DOM order. */
  const eligibleSlides = (slides, context) => [...slides].filter(
    slide => isDesignMode() || isEligible(ruleFromElement(slide), context)
  );

  const PromoBanners = {
    TIER_TAGS,
    parseList,
    isEligible,
    ruleFromElement,
    isDesignMode,
    eligibleSlides
  };

  if (typeof window !== 'undefined') window.PromoBanners = PromoBanners;
  if (typeof module !== 'undefined' && module.exports) module.exports = PromoBanners;

  // Everything below needs a browser. The file is also loaded by the node test,
  // which has no custom-element registry.
  if (typeof customElements === 'undefined' || typeof HTMLElement === 'undefined') return;
  if (customElements.get('promo-carousel')) return;

  class PromoCarousel extends HTMLElement {
    #index = 0;
    #timer = null;
    #scrollEndTimer = null;
    #visible = [];

    connectedCallback() {
      this.viewport = this.querySelector('.promo-carousel__viewport');
      this.dots = this.querySelector('.promo-carousel__dots');
      this.slides = [...this.querySelectorAll('.promo-carousel__slide')];

      this.addEventListener('click', this.#onClick);
      this.addEventListener('pointerenter', () => this.pause());
      this.addEventListener('pointerleave', () => this.play());
      this.addEventListener('focusin', () => this.pause());
      this.addEventListener('focusout', () => this.play());

      this.viewport?.addEventListener('scroll', this.#onScroll, { passive: true });
      document.addEventListener('visibilitychange', this.#onVisibilityChange);
    }

    disconnectedCallback() {
      this.pause();
      document.removeEventListener('visibilitychange', this.#onVisibilityChange);
    }

    /**
     * Apply the customer context: hide the slides they are not eligible for,
     * rebuild the dots, and reveal the carousel -- or leave it hidden when
     * nothing qualifies. Returns the number of slides shown.
     */
    update(context) {
      const allowed = new Set(eligibleSlides(this.slides, context));
      this.#visible = this.slides.filter(slide => {
        slide.hidden = !allowed.has(slide);
        return !slide.hidden;
      });

      const count = this.#visible.length;
      this.hidden = count === 0;
      this.classList.toggle('is-ready', count > 0);
      this.classList.toggle('is-single', count < 2);

      this.#visible.forEach((slide, i) => {
        slide.setAttribute('aria-label', `Promotion ${i + 1} of ${count}`);
      });

      this.#buildDots();
      this.#index = 0;
      if (this.viewport) this.viewport.scrollLeft = 0;
      this.#syncActive();
      this.play();

      return count;
    }

    /** Eligible slides, in DOM order. */
    get visibleSlides() {
      return [...this.#visible];
    }

    get index() {
      return this.#index;
    }

    goTo(index, behavior) {
      const count = this.#visible.length;
      if (count === 0 || !this.viewport) return;

      this.#index = ((index % count) + count) % count;
      const target = this.#visible[this.#index];
      const left = target.offsetLeft - this.viewport.offsetLeft;

      if (typeof this.viewport.scrollTo === 'function') {
        this.viewport.scrollTo({ left, behavior: behavior || this.#scrollBehavior });
      } else {
        this.viewport.scrollLeft = left;
      }
      this.#syncActive();
    }

    next() { this.goTo(this.#index + 1); }
    prev() { this.goTo(this.#index - 1); }

    play() {
      this.pause();
      if (this.#visible.length < 2) return;
      if (this.dataset.autoplay !== 'true') return;
      if (this.#prefersReducedMotion) return;

      const interval = Math.max(parseInt(this.dataset.interval, 10) || 0, 1000);
      this.#timer = setInterval(() => this.next(), interval);
    }

    pause() {
      if (this.#timer) clearInterval(this.#timer);
      this.#timer = null;
    }

    // A backgrounded tab would otherwise burn through every slide unseen.
    #onVisibilityChange = () => {
      if (document.hidden) this.pause(); else this.play();
    };

    get #prefersReducedMotion() {
      return typeof matchMedia === 'function' &&
        matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    get #scrollBehavior() {
      return this.#prefersReducedMotion ? 'auto' : 'smooth';
    }

    #onClick = (event) => {
      const dot = event.target.closest('.promo-carousel__dot');
      if (dot) {
        event.preventDefault();
        this.goTo(parseInt(dot.dataset.index, 10) || 0);
        return;
      }
      const arrow = event.target.closest('.promo-carousel__arrow');
      if (arrow) {
        event.preventDefault();
        arrow.dataset.direction === 'prev' ? this.prev() : this.next();
      }
    };

    // Native scroll-snap has no settled event across all the browsers we
    // support, so debounce and read the position back once the flick stops.
    #onScroll = () => {
      clearTimeout(this.#scrollEndTimer);
      this.#scrollEndTimer = setTimeout(() => {
        this.#index = this.#nearestIndex();
        this.#syncActive();
      }, 120);
    };

    #nearestIndex() {
      if (!this.viewport || this.#visible.length === 0) return 0;
      const position = this.viewport.scrollLeft + this.viewport.offsetLeft;
      let best = 0;
      let bestDistance = Infinity;
      this.#visible.forEach((slide, i) => {
        const distance = Math.abs(slide.offsetLeft - position);
        if (distance < bestDistance) { bestDistance = distance; best = i; }
      });
      return best;
    }

    #buildDots() {
      if (!this.dots) return;

      const showDots = this.dataset.dots === 'true' && this.#visible.length > 1;
      this.dots.hidden = !showDots;
      this.dots.innerHTML = '';
      if (!showDots) return;

      this.#visible.forEach((slide, i) => {
        const dot = this.ownerDocument.createElement('button');
        dot.type = 'button';
        dot.className = 'promo-carousel__dot';
        dot.dataset.index = String(i);
        dot.setAttribute('aria-label', `Show promotion ${i + 1}`);
        this.dots.appendChild(dot);
      });
    }

    #syncActive() {
      if (!this.dots) return;
      this.dots.querySelectorAll('.promo-carousel__dot').forEach((dot, i) => {
        const active = i === this.#index;
        dot.classList.toggle('is-active', active);
        if (active) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
    }
  }

  customElements.define('promo-carousel', PromoCarousel);
})();
